import { useEffect, useState, type ReactNode } from 'react';
import clsx from 'clsx';
import { ChevronRight, Crown, Moon, Sun, Vote } from 'lucide-react';
import { NO_KILL, ROLE_INFO, SCORE_LABELS, SKIP_VOTE, type LobbyView, type PlayerView, type PublicPlayer, type ScoreReason } from '@mafia/core';
import { emit } from '@/lib/socket';
import { PHASE_NAMES, plural, seatLabel } from '@/lib/format';
import { Button, Card, Modal } from '@/components/ui';
import { PlayerCard, type CardInfo, type Mark } from './PlayerCard';
import { Stopwatch, Timer } from './Timer';
import { NightReveal } from './NightReveal';

/** Что сделает кнопка «Дальше» в текущей фазе. */
function advanceLabel(view: PlayerView): string {
  switch (view.phase) {
    case 'ROLE_REVEAL':
      return 'Город засыпает — начать ночь';
    case 'NIGHT':
      return 'Город просыпается — итоги ночи';
    case 'MORNING':
      // Ведущий сам решает, сколько показывать каждую анимацию утра.
      return view.morningStep === 'KILL' && view.lastNightLoverVisit !== null ? 'Показать любовницу' : 'Начать обсуждение';
    case 'DAY_SPEECH':
    case 'TIE_SPEECH':
    case 'LAST_WORD': {
      const i = view.speechOrder.indexOf(view.speakerSeat ?? -1);
      const last = i === view.speechOrder.length - 1;
      if (!last) return `Слово — №${view.speechOrder[i + 1]}`;
      if (view.phase === 'LAST_WORD') return 'Завершить последнее слово';
      if (view.phase === 'TIE_SPEECH') return 'К переголосованию';
      return view.nominations.length ? 'Завершить речи' : 'Завершить день — никто не выставлен';
    }
    case 'DISCUSSION':
      return 'К голосованию';
    case 'VOTING':
      return 'Подвести итоги голосования';
    default:
      return 'Дальше';
  }
}

export function ModeratorScreen({ view, lobby }: { view: PlayerView; lobby: LobbyView | null }) {
  const mod = view.moderator!;
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(false);
  const [closeVote, setCloseVote] = useState(false);
  const [kick, setKick] = useState<PublicPlayer | null>(null);
  const [aborting, setAborting] = useState(false);

  // После смены фазы/спикера кнопка секунду неактивна: второй клик не проскочит следующую фазу.
  useEffect(() => {
    setCooldown(true);
    const t = setTimeout(() => setCooldown(false), 1000);
    return () => clearTimeout(t);
  }, [view.phaseToken]);
  const isNight = view.phase === 'NIGHT';
  const night = mod.night;
  const who = (seat: number | null) => (seat === null ? '—' : seatLabel(view, seat));
  const avatars = new Map(lobby?.members.map((m) => [m.userId, m.avatarId]) ?? []);

  async function advance(confirmed = false) {
    const v = view.voting;
    // Закрыть голосование, пока проголосовали не все, — только после подтверждения.
    if (!confirmed && view.phase === 'VOTING' && v && v.votedCount < v.voterCount) return setCloseVote(true);
    setCloseVote(false);
    setBusy(true);
    await emit('game:advance', { token: view.phaseToken });
    setBusy(false);
  }

  function infoFor(p: PublicPlayer): CardInfo {
    const marks: Mark[] = [];
    const notes: string[] = [];
    if (night) {
      if (night.mafiaDecision === p.seat) marks.push('kill');
      if (night.heal === p.seat) {
        marks.push('heal');
        notes.push('💉 лечат');
      }
      if (night.loverVisit === p.seat) {
        marks.push('visit');
        notes.push('🦋 гость');
      }
      if (night.sheriffCheck === p.seat) notes.push('⭐ проверка');
      if (night.donCheck === p.seat) notes.push('🎩 проверка');
      if (night.maniacKill === p.seat) notes.push('🔪 цель');
      if (night.waiting.includes(p.seat)) notes.push('⏳ ходит');
    }
    const votes = mod.votes;
    const voters = votes ? Object.entries(votes).filter(([, t]) => t === p.seat).map(([v]) => Number(v)) : [];
    return {
      isMe: false,
      hideRole: false,
      avatarId: avatars.get(p.userId) ?? null,
      speaking: view.speakerSeat === p.seat,
      silenced: view.silencedSeat === p.seat,
      nominated: view.nominations.includes(p.seat),
      candidate: view.voting?.candidates.includes(p.seat) ?? false,
      votes: votes ? voters.length : null,
      voters,
      mafiaVoters: night ? Object.entries(night.mafiaVotes).filter(([, t]) => t === p.seat).map(([s]) => Number(s)) : [],
      check: null,
      marks,
      kickable: p.alive,
      onKick: () => setKick(p),
      note: notes.join(' · ') || undefined,
    };
  }

  const scores = [...mod.scores].sort((a, b) => b.total - a.total);

  return (
    <div className={clsx('min-h-[calc(100dvh-3.5rem)] pb-28 transition-colors duration-700', isNight && 'phase-night')}>
      <div className="mx-auto max-w-6xl px-3 py-4 sm:px-4">
        <div className="mb-3 flex items-center gap-3">
          <div className={clsx('flex h-11 w-11 items-center justify-center rounded-xl', isNight ? 'bg-indigo-500/20 text-indigo-300' : 'bg-gold-400/15 text-gold-300')}>
            {isNight ? <Moon className="h-5 w-5" /> : view.phase === 'VOTING' ? <Vote className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
          </div>
          <div className="min-w-0 flex-1">
            <p className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-gold-300">
              <Crown className="h-3.5 w-3.5" /> Вы ведущий · {view.day > 0 ? `${isNight ? 'ночь' : 'день'} ${view.day}` : 'начало игры'}
            </p>
            <h1 className="truncate font-display text-xl sm:text-2xl">{PHASE_NAMES[view.phase]}</h1>
          </div>
          {view.pace === 'manual' ? (
            <Stopwatch since={view.phaseStartedAt} className="text-2xl text-zinc-400 sm:text-3xl" />
          ) : (
            <Timer endsAt={view.phaseEndsAt} className="text-3xl sm:text-4xl" />
          )}
        </div>

        <Announcement view={view} who={who} />
        {/* То же, что сейчас видят игроки: анимация держится, пока вы не нажмёте «Дальше». */}
        <NightReveal view={view} avatars={avatars} inline />

        <div className="grid gap-4 lg:grid-cols-[1fr_340px]">
          <div className="grid h-fit grid-cols-3 gap-2.5 pt-2 sm:grid-cols-4 xl:grid-cols-5">
            {view.players.map((p) => (
              <PlayerCard key={p.seat} p={p} info={infoFor(p)} selectable={false} />
            ))}
          </div>

          <div className="space-y-3">
            {night && (
              <Card className="space-y-1.5 p-4 text-sm">
                <h3 className="mb-1 font-medium text-zinc-300">Ночные ходы</h3>
                <Row label="🕶️ Мафия">
                  {Object.keys(night.mafiaVotes).length === 0
                    ? 'ещё не выбрала'
                    : Object.entries(night.mafiaVotes)
                        .map(([m, t]) => `№${m}→${t === NO_KILL ? 'не убивать' : `№${t}`}`)
                        .join(', ')}
                  {' · '}
                  <b className={night.mafiaDecision ? 'text-blood-400' : 'text-zinc-500'}>
                    {night.mafiaDecision === NO_KILL ? 'не убивают' : night.mafiaDecision ? `цель ${who(night.mafiaDecision)}` : 'промах'}
                  </b>
                </Row>
                <Row label="🎩 Дон проверяет">{checkText(view, night.donCheck, mod.donChecks, 'шериф')}</Row>
                <Row label="⭐ Шериф проверяет">{checkText(view, night.sheriffCheck, mod.sheriffChecks, 'мафия')}</Row>
                <Row label="💉 Доктор лечит">{who(night.heal)}</Row>
                <Row label="🦋 Любовница у">{who(night.loverVisit)}</Row>
                {night.maniacKill !== null && <Row label="🔪 Маньяк">{who(night.maniacKill)}</Row>}
                <p className={clsx('pt-1 text-xs', night.waiting.length ? 'text-gold-300' : 'text-emerald-300')}>
                  {night.waiting.length
                    ? `Ещё не сходили: ${night.waiting.map((s) => `${who(s)} (${roleOf(view, s)})`).join(', ')}`
                    : 'Все активные роли сходили'}
                </p>
              </Card>
            )}

            {view.voting && (
              <Card className="p-4 text-sm">
                <h3 className="mb-2 font-medium text-zinc-300">
                  Голосование{view.voting.round === 2 ? ' (повторное)' : ''} · {view.voting.votedCount} из {view.voting.voterCount}
                </h3>
                {[...view.voting.candidates, ...(view.voting.canSkip ? [SKIP_VOTE] : [])].map((c) => {
                  const voters = Object.entries(mod.votes ?? {}).filter(([, t]) => t === c).map(([v]) => `№${v}`);
                  return (
                    <p key={c} className="flex justify-between gap-2">
                      <span>{c === SKIP_VOTE ? 'Пропустить' : who(c)}</span>
                      <span className="text-zinc-500">
                        {voters.length} {plural(voters.length, 'голос', 'голоса', 'голосов')}
                        {voters.length ? ` (${voters.join(', ')})` : ''}
                      </span>
                    </p>
                  );
                })}
              </Card>
            )}

            {view.nominations.length > 0 && (
              <Card className="p-4 text-sm">
                <h3 className="mb-1 font-medium text-zinc-300">Выставлены</h3>
                <p className="text-zinc-400">{view.nominations.map(who).join(', ')}</p>
              </Card>
            )}

            <Card className="p-4 text-sm">
              <h3 className="mb-2 font-medium text-zinc-300">Баллы</h3>
              <div className="space-y-1">
                {scores.map((s) => (
                  <p
                    key={s.seat}
                    className="flex justify-between gap-2"
                    title={s.entries.map((e) => `${SCORE_LABELS[e.reason as ScoreReason] ?? e.reason}: ${e.points > 0 ? '+' : ''}${e.points}`).join('\n')}
                  >
                    <span className="truncate text-zinc-400">{who(s.seat)}</span>
                    <b className={s.total < 0 ? 'text-blood-400' : s.total > 0 ? 'text-gold-300' : 'text-zinc-500'}>{s.total}</b>
                  </p>
                ))}
              </div>
            </Card>
          </div>
        </div>
      </div>

      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-white/5 bg-ink-950/90 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center gap-2 px-3 py-3 pb-[max(.75rem,env(safe-area-inset-bottom))] sm:px-4">
          <Button size="lg" variant="gold" className="flex-1 sm:flex-none" loading={busy} disabled={cooldown} onClick={() => advance()}>
            {advanceLabel(view)} <ChevronRight className="h-5 w-5" />
          </Button>
          <span className="hidden flex-1 text-sm text-zinc-500 md:block">
            {view.pace === 'manual' ? 'Ручной режим: фазы переключаете вы.' : 'Таймеры идут сами; кнопка переключает раньше времени.'}
          </span>
          <Button variant="danger" onClick={() => setAborting(true)}>
            Прервать игру
          </Button>
        </div>
      </div>

      <Modal open={closeVote} onClose={() => setCloseVote(false)} title="Подвести итоги сейчас?">
        <p className="mb-5 text-sm text-zinc-400">
          Проголосовали {view.voting?.votedCount ?? 0} из {view.voting?.voterCount ?? 0}. Не проголосовавшие не повлияют на итог. Голосование
          закроется само, когда проголосуют все.
        </p>
        <div className="flex gap-2">
          <Button variant="primary" className="flex-1" loading={busy} onClick={() => advance(true)}>
            Подвести итоги
          </Button>
          <Button variant="secondary" className="flex-1" onClick={() => setCloseVote(false)}>
            Подождать
          </Button>
        </div>
      </Modal>

      <Modal open={kick !== null} onClose={() => setKick(null)} title={`Удалить ${kick ? seatLabel(view, kick.seat) : ''}?`}>
        <p className="mb-5 text-sm text-zinc-400">Игрок выбывает из партии, как если бы вышел сам. Вернуть его будет нельзя.</p>
        <div className="flex gap-2">
          <Button
            variant="primary"
            className="flex-1"
            onClick={async () => {
              const seat = kick!.seat;
              setKick(null);
              await emit('game:kick', { seat });
            }}
          >
            Удалить
          </Button>
          <Button variant="secondary" className="flex-1" onClick={() => setKick(null)}>
            Отмена
          </Button>
        </div>
      </Modal>

      <Modal open={aborting} onClose={() => setAborting(false)} title="Прервать игру?">
        <p className="mb-5 text-sm text-zinc-400">Партия закончится без победителя и не попадёт в статистику и рейтинг.</p>
        <div className="flex gap-2">
          <Button
            variant="primary"
            className="flex-1"
            onClick={async () => {
              setAborting(false);
              await emit('game:abort');
            }}
          >
            Прервать
          </Button>
          <Button variant="secondary" className="flex-1" onClick={() => setAborting(false)}>
            Продолжить игру
          </Button>
        </div>
      </Modal>
    </div>
  );
}

/** Подсказка ведущему: что объявить столу в этой фазе. */
function Announcement({ view, who }: { view: PlayerView; who: (seat: number | null) => string }) {
  let text: ReactNode = null;
  switch (view.phase) {
    case 'ROLE_REVEAL':
      text = 'Игроки знакомятся с ролями. Когда все готовы — объявите ночь.';
      break;
    case 'NIGHT':
      text = 'Город спит. Порядок: мафия и дон выбирают жертву → дон проверяет → шериф проверяет → доктор лечит → любовница выбирает гостя.';
      break;
    case 'MORNING':
      text = (
        <>
          Объявите:{' '}
          <b className={view.lastNightDeaths.length ? 'text-blood-400' : 'text-emerald-300'}>
            {view.lastNightDeaths.length ? `погибли ${view.lastNightDeaths.map(who).join(', ')}` : 'никто не погиб'}
          </b>
          {view.lastNightLoverVisit !== null && (
            <>
              ; любовница была с <b className="text-pink-300">{who(view.lastNightLoverVisit)}</b>
              {view.lastNightWithLover !== null
                ? ' — к ней пришла мафия, гость погиб вместе с ней'
                : view.silencedSeat !== null
                  ? ' — он сегодня молчит и не голосует'
                  : ''}
            </>
          )}
          .
        </>
      );
      break;
    case 'DAY_SPEECH':
    case 'TIE_SPEECH':
    case 'LAST_WORD':
      text = (
        <>
          Говорит <b className="text-gold-300">{who(view.speakerSeat)}</b>. Очередь: {view.speechOrder.map((s) => `№${s}`).join(' → ')}
          {view.silencedSeat !== null ? ` · молчит ${who(view.silencedSeat)}` : ''}
        </>
      );
      break;
    case 'DISCUSSION':
      text = (
        <>
          Общее обсуждение. На голосовании: <b className="text-gold-300">{view.nominations.map(who).join(', ')}</b>. Затем — голосование.
        </>
      );
      break;
    case 'VOTING':
      text = view.voting?.canSkip ? 'Стол голосует. Пропуск голосования ещё доступен (один раз за игру).' : 'Стол голосует.';
      break;
  }
  if (!text) return null;
  return <div className="mb-3 rounded-xl border border-gold-400/20 bg-gold-400/5 px-4 py-3 text-sm leading-relaxed text-zinc-200">{text}</div>;
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <p className="flex gap-2">
      <span className="w-36 shrink-0 text-zinc-500">{label}</span>
      <span>{children}</span>
    </p>
  );
}

function roleOf(view: PlayerView, seat: number) {
  const role = view.players.find((p) => p.seat === seat)?.role;
  return role ? ROLE_INFO[role].name : '?';
}

function checkText(view: PlayerView, target: number | null, history: { day: number; target: number; positive: boolean }[], positiveLabel: string) {
  if (target === null) return '—';
  const rec = history.find((c) => c.day === view.day && c.target === target);
  return `${seatLabel(view, target)}${rec ? ` → ${rec.positive ? positiveLabel : 'нет'}` : ''}`;
}
